import React, { useState, useEffect } from 'react';
import { MdClose, MdAssessment, MdPerson, MdLocationOn, MdTrendingUp, MdAttachMoney, MdEdit, MdSave } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const OfficerPerformanceModal = ({ isOpen, onClose, officer, userMmdaId, user }) => {
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetAmount, setTargetAmount] = useState('');
  const [showPopup, setShowPopup] = useState(null);



  useEffect(() => {
    if (isOpen && officer) {
      fetchPerformanceData();
    }
  }, [isOpen, officer]);

  // Add escape key handler
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {

        handleClose(e);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/officer-performance/officer/${officer.officer_id}`, API_CONFIG);
      setPerformanceData(response.data);

      // Set current target amount for editing
      if (response.data.current_target) {
        setTargetAmount(response.data.current_target.target_amount);
      }
    } catch (error) {

      setShowPopup({ message: 'Failed to load performance data', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTarget = async () => {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);

      if (performanceData.current_target) {
        // Update existing target
        await axios.put(`${API_BASE_URL}/officer-performance/targets/${performanceData.current_target.target_id}`, {
          target_amount: parseFloat(targetAmount),
          target_description: `Monthly target for ${currentMonth}`,
          status: 'active'
        }, API_CONFIG);
      } else {
        // Create new target
        await axios.post(`${API_BASE_URL}/officer-performance/targets`, {
          officer_id: officer.officer_id,
          target_type: 'monthly',
          target_amount: parseFloat(targetAmount),
          target_period: currentMonth,
          target_description: `Monthly target for ${currentMonth}`,
          created_by: user?.user_id || userMmdaId // MMDA Admin user_id
        }, API_CONFIG);
      }

      setEditingTarget(false);
      setShowPopup({ message: 'Target updated successfully', type: 'success' });
      fetchPerformanceData(); // Refresh data
    } catch (error) {
      console.error('Error saving target:', error);
      setShowPopup({ message: 'Failed to save target', type: 'error' });
    }
  };

  const handleCalculatePerformance = async () => {
    try {
      const currentMonth = new Date().toISOString().slice(0, 7);
      await axios.post(`${API_BASE_URL}/officer-performance/calculate`, {
        officer_id: officer.officer_id,
        period_value: currentMonth,
        period_type: 'monthly'
      }, API_CONFIG);

      setShowPopup({ message: 'Performance calculated successfully', type: 'success' });
      fetchPerformanceData(); // Refresh data
    } catch (error) {
      console.error('Error calculating performance:', error);
      setShowPopup({ message: 'Failed to calculate performance', type: 'error' });
    }
  };

  const handleClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    console.log('OfficerPerformanceModal: Close button clicked');
    setEditingTarget(false); // Reset any editing state
    setShowPopup(null); // Clear any popups
    setPerformanceData(null); // Clear performance data
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      console.log('OfficerPerformanceModal: Overlay clicked');
      handleClose(e);
    }
  };

  if (!isOpen || !officer) return null;

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={handleOverlayClick}
      >
        <div style={{
          backgroundColor: 'white',
          padding: 40,
          borderRadius: 8,
          textAlign: 'center'
        }}
        onClick={(e) => e.stopPropagation()}
        >
          <p>Loading performance data...</p>
          <button
            onClick={handleClose}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              border: 'none',
              borderRadius: 4,
              backgroundColor: '#6c757d',
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!performanceData) return null;

  const currentPerformance = performanceData.current_performance || {};
  const currentTarget = performanceData.current_target || {};
  const progressPercentage = parseFloat(currentTarget.target_amount || 0) > 0
    ? (parseFloat(currentPerformance.total_collected || 0) / parseFloat(currentTarget.target_amount || 0)) * 100
    : 0;

  // Responsive styles
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 100000,
      padding: '10px',
      boxSizing: 'border-box',
    },
    modal: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      width: '100%',
      maxWidth: '800px',
      maxHeight: '95vh',
      overflowY: 'auto',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
      position: 'relative',
      margin: 'auto',
    }
  };

  // Apply responsive adjustments based on window width
  if (typeof window !== 'undefined') {
    if (window.innerWidth <= 768) {
      modalStyles.modal.padding = '16px';
      modalStyles.modal.borderRadius = '8px';
      modalStyles.modal.maxHeight = '98vh';
      modalStyles.overlay.padding = '5px';
    }
    if (window.innerWidth <= 480) {
      modalStyles.modal.padding = '12px';
      modalStyles.modal.borderRadius = '6px';
      modalStyles.modal.maxHeight = '100vh';
      modalStyles.overlay.padding = '0px';
    }
  }

  return (
    <div
      style={modalStyles.overlay}
      onClick={handleOverlayClick}
    >
      <div
        style={modalStyles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 16,
          borderBottom: '1px solid #e0e0e0',
          flexWrap: 'wrap',
          gap: '10px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flex: '1',
            minWidth: '200px'
          }}>
            <MdAssessment size={24} style={{ color: '#1976d2' }} />
            <h2 style={{
              margin: 0,
              color: '#333',
              fontSize: 'clamp(16px, 4vw, 20px)',
              fontWeight: 'bold'
            }}>
              Officer Performance
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: '#f5f5f5',
              border: '2px solid #ddd',
              cursor: 'pointer',
              padding: '10px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = '#e0e0e0';
              e.target.style.borderColor = '#bbb';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = '#f5f5f5';
              e.target.style.borderColor = '#ddd';
            }}
            title="Close Modal"
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Officer Info */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: 16,
          borderRadius: 8,
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}>
          <div style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MdPerson size={30} style={{ color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 4px 0', color: '#333', fontSize: 18 }}>
              {officer.first_name} {officer.last_name}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#666', fontSize: 14 }}>
              <MdLocationOn size={16} />
              <span>{officer.zone_name}</span>
            </div>
            <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: 12 }}>
              {officer.phone} • {officer.email}
            </p>
          </div>
        </div>

        {/* Performance Metrics */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          marginBottom: 20,
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            gap: 12
          },
          '@media (max-width: 480px)': {
            gap: 10
          }
        }}>
          {/* Total Collections */}
          <div style={{
            backgroundColor: '#e8f5e8',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #c8e6c9',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MdAttachMoney size={20} style={{ color: '#2e7d32' }} />
              <h4 style={{ margin: 0, color: '#2e7d32', fontSize: 14 }}>Total Collections</h4>
            </div>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#2e7d32' }}>
              GH₵ {parseFloat(currentPerformance.total_collected || 0).toLocaleString()}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#2e7d32' }}>
              This month
            </p>
          </div>

          {/* Target Progress */}
          <div style={{
            backgroundColor: '#e3f2fd',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #bbdefb',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MdTrendingUp size={20} style={{ color: '#1976d2' }} />
              <h4 style={{ margin: 0, color: '#1976d2', fontSize: 14 }}>Target Progress</h4>
            </div>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#1976d2' }}>
              {progressPercentage.toFixed(1)}%
            </p>
            <div style={{
              width: '100%',
              height: 8,
              backgroundColor: '#bbdefb',
              borderRadius: 4,
              marginTop: 8,
              overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.min(progressPercentage, 100)}%`,
                height: '100%',
                backgroundColor: '#1976d2',
                transition: 'width 0.3s ease',
              }} />
            </div>
          </div>

          {/* Transactions */}
          <div style={{
            backgroundColor: '#fff3e0',
            padding: 16,
            borderRadius: 8,
            border: '1px solid #ffcc02',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <MdAssessment size={20} style={{ color: '#f57c00' }} />
              <h4 style={{ margin: 0, color: '#f57c00', fontSize: 14 }}>Transactions</h4>
            </div>
            <p style={{ margin: 0, fontSize: 24, fontWeight: 'bold', color: '#f57c00' }}>
              {parseInt(currentPerformance.transactions_count || 0)}
            </p>
            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#f57c00' }}>
              Avg: GH₵ {parseFloat(currentPerformance.average_per_transaction || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Performance Details */}
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: 16,
          borderRadius: 8,
          border: '1px solid #e9ecef',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, color: '#333', fontSize: 16 }}>Performance Summary</h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleCalculatePerformance}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Refresh Performance
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Monthly Target</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                {editingTarget ? (
                  <>
                    <input
                      type="number"
                      value={targetAmount}
                      onChange={(e) => setTargetAmount(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        borderRadius: 4,
                        fontSize: 14,
                        width: 120,
                      }}
                      placeholder="Enter target"
                    />
                    <button
                      onClick={handleSaveTarget}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <MdSave size={14} />
                    </button>
                    <button
                      onClick={() => setEditingTarget(false)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <MdClose size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ margin: 0, fontSize: 14, color: '#333' }}>
                      GH₵ {parseFloat(currentTarget.target_amount || 0).toLocaleString()}
                    </p>
                    <button
                      onClick={() => setEditingTarget(true)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#ffc107',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        fontSize: 12,
                        cursor: 'pointer',
                      }}
                    >
                      <MdEdit size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Achievement Rate</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#333' }}>
                {parseFloat(currentPerformance.achievement_percentage || 0).toFixed(1)}%
              </p>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Performance Rating</label>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: 14,
                color: currentPerformance.performance_rating === 'excellent' ? '#2e7d32' :
                       currentPerformance.performance_rating === 'good' ? '#1976d2' :
                       currentPerformance.performance_rating === 'average' ? '#f57c00' : '#c62828',
                textTransform: 'capitalize',
                fontWeight: 'bold'
              }}>
                {currentPerformance.performance_rating || 'Pending'}
              </p>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#666', fontWeight: 'bold' }}>Period</label>
              <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#333' }}>
                {currentPerformance.period_value || new Date().toISOString().slice(0, 7)}
              </p>
            </div>
          </div>
        </div>

        {/* Performance Status */}
        <div style={{
          marginTop: 20,
          padding: 16,
          backgroundColor: progressPercentage >= 90 ? '#e8f5e8' : progressPercentage >= 70 ? '#fff3e0' : '#ffebee',
          borderRadius: 8,
          border: `1px solid ${progressPercentage >= 90 ? '#c8e6c9' : progressPercentage >= 70 ? '#ffcc02' : '#ffcdd2'}`,
        }}>
          <h3 style={{ 
            margin: '0 0 8px 0', 
            color: progressPercentage >= 90 ? '#2e7d32' : progressPercentage >= 70 ? '#f57c00' : '#c62828', 
            fontSize: 14, 
            fontWeight: 'bold' 
          }}>
            Performance Status: {progressPercentage >= 90 ? 'Excellent' : progressPercentage >= 70 ? 'Good' : 'Needs Improvement'}
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: 12, 
            color: progressPercentage >= 90 ? '#2e7d32' : progressPercentage >= 70 ? '#f57c00' : '#c62828' 
          }}>
            {progressPercentage >= 90 
              ? 'This officer is performing excellently and exceeding expectations.'
              : progressPercentage >= 70 
              ? 'This officer is performing well and meeting most targets.'
              : 'This officer may need additional support to improve performance.'
            }
          </p>
        </div>

        {/* Close Button */}
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleClose}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: 4,
              backgroundColor: '#1976d2',
              color: 'white',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 'bold',
            }}
          >
            Close
          </button>
        </div>

        {/* Popup Notification */}
        {showPopup && (
          <div style={{
            position: 'fixed',
            top: 20,
            right: 20,
            backgroundColor: showPopup.type === 'success' ? '#d4edda' : '#f8d7da',
            color: showPopup.type === 'success' ? '#155724' : '#721c24',
            padding: 12,
            borderRadius: 4,
            border: `1px solid ${showPopup.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
            zIndex: 100001,
            maxWidth: 300,
          }}>
            {showPopup.message}
            <button
              onClick={() => setShowPopup(null)}
              style={{
                marginLeft: 10,
                background: 'none',
                border: 'none',
                color: 'inherit',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 'bold',
              }}
            >
              ×
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficerPerformanceModal;
